import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "risa-oasis-ui_v2";
import { getFileDownloadUrl } from "../../../../api/firebase/firestoreService";
import { LoaderMessage } from "../../../../components/loaderMessage/loaderMessage";
import ImageviewerModal from "../../../../components/modals/imageViewerModal/imageviewerModal";
import { ToastMessages } from "../../../../components/toasts/toastMessages";
import { useDataAccumulatorForSingleOrder } from "../../../../context/dataAccumulatorForSingleOrder";
import { usePayerNameContextFromSingleOrderLayout } from "../../../../context/payerNameContextFromSingleOrderLayout";
import { SubmissionStatusEnum } from "../../../../data-model/submissionDocs";
import { ScreenNamesForBigQuery } from "../../../../enums/eventsData";
import { EventsName } from "../../../../enums/eventsName";
import { ModalId } from "../../../../enums/modalId";
import { SingleOrderDetailsTableName } from "../../../../enums/singleOrderDetailsTableName";
import { UserGeneralInfoContextKeys } from "../../../../enums/userGeneralInfoContextKeys";
import { useUpdateOrderDetails } from "../../../../hooks/singleMedicalPaOrderDetails/useUpdateOrderDetails";
import { useModalOpener } from "../../../../hooks/useModalOpener";
import {
  closeModal,
  setOpenedModalId,
} from "../../../../redux/slice/modalSlice";
import { AppDispatch, RootState } from "../../../../redux/store/store";
import { UserStore } from "../../../../store/UserGeneralInfoStore";
import { InfoIcon } from "../../../../svg/info-icon";
import { logError } from "../../../../utils/customLogger";
import { downloadFileFromUrl } from "../../../../utils/fileDownloader";
import { generateFullName } from "../../../../utils/generateOrderDataForTable";
import { getOrgIdForFetchExternalWorklist } from "../../../../utils/organizationHelper";
import { apiWorkerManager } from "../../../../workerManagers/workerManager";
import { useRefreshFaxStatus } from "../../hooks/useRefreshFaxStatus";
import { useSubmissionAgentSteps } from "../../hooks/useSubmissionAgentSteps";
import {
  canMarkAsSubmitted,
  hasDocumentActions,
  hasSidePanel,
  resolveSubmissionDisplayState,
} from "../../utils/submissionDisplayState";
import {
  fetchClinicalDocumentPathFallback,
  fetchFaxOutputFallback,
} from "../../utils/submissionFirestoreFallback";
import AuthInfoComponent from "../authInfoComponent/authInfoComponent";
import CptComponentCodes from "../cptCodesComponent/cptComponentCodes";
import StatusBanner from "../statusBanner/statusBanner";
import SubmissionSidePanel from "../submissionSidePanel/submissionSidePanel";

interface SubmissionStatusMainProps {
  id: string;
}

/**
 * Orchestrates the submission status screen: fetches the order, derives the
 * current submission status, and renders the page title, patient header,
 * status banner, auth information, CPT codes table, and footer actions.
 *
 * @input props - The order id whose submission status is displayed.
 * @returns The submission status screen, a loader, or an error placeholder.
 */
const SubmissionStatusMain = (props: SubmissionStatusMainProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isFaxSubmitted } = location.state || {};
  const dispatch = useDispatch<AppDispatch>();
  const {
    generalOrderDetails,
    generalOrderDetailsLoading,
    generalOrderDetailsError,
    workflowConfig,
    workflowConfigLoading,
    fetchAll,
  } = useDataAccumulatorForSingleOrder();
  const { openedModalId, metaData } = useSelector(
    (state: RootState) => state.modal,
  );
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const { payerName } = usePayerNameContextFromSingleOrderLayout();
  const orgId = getOrgIdForFetchExternalWorklist();

  useModalOpener();

  const { refreshFaxStatus, faxStatusState } = useRefreshFaxStatus({
    orderId: props.id,
  });
  const { runUpdate: updateOrderStatus } = useUpdateOrderDetails<{
    submission_status: string | null;
  }>({
    orderId: props.id,
    orgId: orgId ?? "",
    tableName: SingleOrderDetailsTableName.ORDER_STATUS,
  });
  const hasLoggedToBigQuery = React.useRef(false);
  const [resubmitLoading, setResubmitLoading] = React.useState(false);
  const [markSubmittedLoading, setMarkSubmittedLoading] = React.useState(false);
  const [downloadingDocument, setDownloadingDocument] = React.useState(false);
  const [faxId, setFaxId] = React.useState<string>("");

  const order = generalOrderDetails?.order;

  // Postgres stores portal and fax outcomes in the single `submission_status`
  // column, so there is no longer a separate fax field to read from.
  // Null/empty means no filing attempt yet (shown as "Not Submitted").
  const submissionStatus = (order?.status?.submission_status ?? "") as
    SubmissionStatusEnum | "";

  const displayState = resolveSubmissionDisplayState(
    submissionStatus,
    Boolean(isFaxSubmitted),
  );

  const {
    agentSteps,
    lastScreenshotPath,
    portalLoginUrl,
    loading: agentStepsLoading,
  } = useSubmissionAgentSteps({
    orderId: props.id,
    submissionType: workflowConfig?.submission_type,
    displayState,
  });

  const handleViewScreenshot = () => {
    if (!lastScreenshotPath) {
      return;
    }
    dispatch(
      setOpenedModalId({
        id: ModalId.IMAGE_VIEWER_MODAL,
        metaData: { filePaths: [lastScreenshotPath] },
      }),
    );
  };

  /**
   * Resolves the storage path of the downloadable submission PDF.
   *
   * Portal attachments prefer Postgres (`order.clinical_document.file_path`),
   * then Firestore. Fax PDFs use PG `submission_output.filled_pdf_link` with
   * Firestore `submission_info/output` fallback via {@link fetchFaxOutputFallback}.
   *
   * @returns Firebase Storage path for the submission document, when present.
   */
  const resolveSubmissionDocumentPath = async (): Promise<
    string | undefined
  > => {
    const postgresClinicalPath = order?.clinical_document?.file_path;
    if (postgresClinicalPath) {
      return postgresClinicalPath;
    }

    const firestoreClinicalPath = await fetchClinicalDocumentPathFallback(
      props.id,
    );
    if (firestoreClinicalPath) {
      return firestoreClinicalPath;
    }

    const faxOutput = await fetchFaxOutputFallback(props.id);
    return faxOutput.filled_pdf_link;
  };

  /**
   * Resolves the document path, fetches the file, and triggers the browser
   * download. Path resolution plus the storage fetch can take several seconds,
   * so `downloadingDocument` drives the banner's spinner for the whole span.
   *
   * @returns Promise that resolves once the download starts or fails.
   */
  const handleDownloadDocument = async () => {
    if (downloadingDocument) {
      return;
    }
    setDownloadingDocument(true);
    try {
      const storagePath = await resolveSubmissionDocumentPath();

      if (!storagePath) {
        toast.error(ToastMessages.SUBMISSION_DOCUMENT_NOT_FOUND);
        return;
      }

      // `filled_pdf_link` is a Firebase Storage path, not a fetchable URL, so
      // it must be resolved to a signed download URL before fetching.
      const downloadUrl = await getFileDownloadUrl(storagePath);
      if (!downloadUrl) {
        toast.error(ToastMessages.SUBMISSION_DOCUMENT_URL_ERROR);
        return;
      }
      await downloadFileFromUrl(downloadUrl, `submission-${props.id}.pdf`);
    } catch (error) {
      logError(error as Error, "Error downloading submission document");
      toast.error(ToastMessages.SUBMISSION_DOCUMENT_DOWNLOAD_ERROR);
    } finally {
      setDownloadingDocument(false);
    }
  };

  React.useEffect(() => {
    if (props.id) {
      fetchAll(props.id, orgId ?? "");
    }
  }, [props.id, orgId, fetchAll]);

  React.useEffect(() => {
    // Fax id from PG submission_output (Firestore fallback) for the delivered banner.
    if (displayState !== "fax_delivered") {
      setFaxId("");
      return;
    }
    let isActive = true;
    fetchFaxOutputFallback(props.id)
      .then((output) => {
        if (isActive) {
          setFaxId(
            output.fax_details_id != null ? String(output.fax_details_id) : "",
          );
        }
      })
      .catch((error) => {
        logError(error as Error, "Fax info record not found");
      });
    return () => {
      isActive = false;
    };
  }, [displayState, props.id]);

  React.useEffect(
    () => () => {
      dispatch(closeModal());
    },
    [dispatch],
  );

  React.useEffect(() => {
    if (order && !hasLoggedToBigQuery.current) {
      hasLoggedToBigQuery.current = true;
      apiWorkerManager.enqueueAndProcess({
        patient_id:
          order?.demographics?.patient_id ?? order?.payload?.patient_id ?? "",
        order_id: order?.order_id ?? props.id,
        org_id: orgId ?? "",
        event_name: EventsName.SUBMISSION_STATUS_SCREEN_SHOWN,
        user_id: user?.id ?? "",
        user_email: user?.email ?? "",
        additional_data: {
          screen_name: ScreenNamesForBigQuery.SUBMISSION_STATUS_SCREEN,
          source: ScreenNamesForBigQuery.SUBMISSION_STATUS_SCREEN,
          payer_name: payerName,
          regimen_name: order?.payload?.regimen_name ?? "",
        },
      });
    }
  }, [order]);

  const handleBackToOrders = () => {
    const parentTable = UserStore.getItem(
      UserGeneralInfoContextKeys.PARENT_TABLE,
    );
    navigate(parentTable ? `/worklists/${parentTable}` : "/worklists/");
  };

  /**
   * Re-runs the fax status refresh (including PG `submission_status`), then
   * re-fetches the Postgres order data so the banner reflects the new value.
   *
   * @returns Promise that resolves once the refresh and re-fetch complete.
   */
  const handleRefreshStatus = async () => {
    await refreshFaxStatus();
    await fetchAll(props.id, orgId ?? "");
  };

  /**
   * Clears the order's Postgres `submission_status` and routes back to the CPT
   * codes step so the user can re-file the authorization from scratch.
   *
   * @returns Promise that resolves once the status is cleared and navigation runs.
   */
  const handleResubmit = async () => {
    if (!props.id) {
      toast.error(ToastMessages.ORDER_ID_NOT_FOUND);
      return;
    }
    setResubmitLoading(true);
    try {
      // Clear the status to NULL (not ""), matching the backend's "no
      // submission" representation so reads and status guards stay consistent.
      const result = await updateOrderStatus({ submission_status: null });
      if (!result.success) {
        toast.error(ToastMessages.STATUS_UPDATE_ERROR);
        return;
      }
      navigate(`/worklists/singleOrder/${props.id}/cptCodes`);
    } finally {
      setResubmitLoading(false);
    }
  };

  /**
   * Marks the order's Postgres `submission_status` as manually submitted and
   * returns to the worklist, used when the user has confirmed the auth on the
   * payer portal.
   *
   * @returns Promise that resolves once the status is updated and navigation runs.
   */
  const handleMarkAsSubmitted = async () => {
    if (!props.id) {
      toast.error(ToastMessages.ORDER_ID_NOT_FOUND);
      return;
    }
    setMarkSubmittedLoading(true);
    try {
      const result = await updateOrderStatus({
        submission_status: "submission_completed",
      });
      if (!result.success) {
        toast.error(ToastMessages.STATUS_UPDATE_ERROR);
        return;
      }
      handleBackToOrders();
    } finally {
      setMarkSubmittedLoading(false);
    }
  };

  if (
    generalOrderDetailsLoading ||
    workflowConfigLoading ||
    (!generalOrderDetails && !generalOrderDetailsError)
  ) {
    return <LoaderMessage message="Loading submission status..." />;
  }

  if (!order) {
    return <SubmissionDocErrorComponent />;
  }

  // Show the fax refresh action for fax flows: either the screen was opened from
  // the fax submission flow, or Postgres reports a fax `submission_status`.
  const showFaxRefresh =
    Boolean(isFaxSubmitted) ||
    (Boolean(submissionStatus) && submissionStatus.startsWith("fax"));

  return (
    <div className="submission-status--container flex h-full w-full flex-col overflow-hidden">
      <div className="submission-status--card flex flex-1 flex-col overflow-hidden rounded-lg bg-white">
        <div className="submission-status--content flex min-h-0 flex-1 flex-col gap-4 overflow-hidden p-6">
          <div className="submission-status-form__body--header flex shrink-0 items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <div className="text-h11 font-bold uppercase leading-6 text-primaryGray-1">
                {generateFullName(
                  order?.demographics?.first_name ?? "",
                  "",
                  order?.demographics?.last_name ?? "",
                )}
              </div>
              <div className="text-small font-semibold leading-small text-primaryGray-1">
                (
                {order?.demographics?.patient_id ??
                  order?.payload?.patient_id ??
                  ""}
                )
              </div>
            </div>
            <div className="flex items-center gap-2">
              {showFaxRefresh && (
                <Button
                  buttonType="secondary"
                  size="medium"
                  disabled={faxStatusState.loading ?? false}
                  onClick={handleRefreshStatus}
                >
                  {faxStatusState.loading ? "Refreshing..." : "Refresh Status"}
                </Button>
              )}
              {submissionStatus && (
                <Button
                  buttonType="secondary"
                  size="medium"
                  disabled={resubmitLoading}
                  onClick={handleResubmit}
                >
                  {resubmitLoading ? "Re Submitting..." : "Re Submit"}
                </Button>
              )}
            </div>
          </div>

          <div className="shrink-0">
            <StatusBanner
              status={submissionStatus}
              submissionType={workflowConfig?.submission_type}
              showDocumentActions={hasDocumentActions(displayState)}
              onViewScreenshot={
                showFaxRefresh ? undefined : handleViewScreenshot
              }
              onDownloadDocument={handleDownloadDocument}
              isDownloadingDocument={downloadingDocument}
              faxId={displayState === "fax_delivered" ? faxId : undefined}
            />
          </div>

          <div className="submission-status--body flex min-h-0 flex-1 flex-row items-start gap-6 overflow-hidden">
            <div
              className={`submission-status--details-box flex max-h-full flex-col gap-6 self-start overflow-y-auto rounded-md border border-primaryGray-15 bg-white p-4 ${
                hasSidePanel(displayState) ? "w-[70%]" : "w-full"
              }`}
            >
              <AuthInfoComponent />

              <CptComponentCodes />
            </div>

            <SubmissionSidePanel
              state={displayState}
              agentSteps={agentSteps}
              portalLoginUrl={portalLoginUrl}
              agentStepsLoading={agentStepsLoading}
            />
          </div>
        </div>

        {openedModalId === ModalId.IMAGE_VIEWER_MODAL && (
          <ImageviewerModal
            fileUrls={[]}
            filePaths={metaData?.filePaths ?? []}
            onClose={() => dispatch(closeModal())}
            shouldHideZoom={false}
          />
        )}

        <div className="submission-status--footer flex items-center justify-end gap-2 border-t border-primaryGray-15 px-6 py-3">
          <div className="flex items-center gap-2">
            <Button
              buttonType="secondary"
              size="medium"
              disabled={markSubmittedLoading}
              onClick={handleBackToOrders}
            >
              Back
            </Button>
            {canMarkAsSubmitted(displayState) && (
              <Button
                buttonType="primary"
                size="medium"
                disabled={markSubmittedLoading}
                onClick={handleMarkAsSubmitted}
              >
                {markSubmittedLoading ? "Marking..." : "Mark as Submitted"}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Placeholder shown while the submission status document is not yet available.
 *
 * @returns The "please wait / retry" error placeholder.
 */
const SubmissionDocErrorComponent = () => {
  const navigate = useNavigate();
  return (
    <div className="error-container flex h-full w-full flex-col items-center justify-center gap-2 bg-white">
      <div className="error-icon">
        <InfoIcon height={"48"} width={"48"} stroke={"#0056D6"} />
      </div>
      <div className="error-message text-center text-h11 font-semiBold text-primaryGray-1">
        Please wait while we retrieve the submission status. <br />
        You may revisit this page after a few minutes for the updated status.
      </div>
      <div className="error-message button mt-4">
        <Button
          disabled={false}
          onClick={() => navigate("/worklists")}
          buttonType={"primary"}
          size={"medium"}
        >
          Back to Home page
        </Button>
      </div>
    </div>
  );
};

export default SubmissionStatusMain;
